import { createContext, useContext, useMemo } from 'react';
import apiService from '../services/apiService';

const AppContext = createContext(null);
const CACHE_TTL_MS = 30000;
const responseCache = new Map();
const inflightRequests = new Map();

function getCachedRequest(key, request, ttl = CACHE_TTL_MS) {
  const cached = responseCache.get(key);

  if (cached && Date.now() - cached.timestamp < ttl) {
    return Promise.resolve(cached.data);
  }

  if (inflightRequests.has(key)) {
    return inflightRequests.get(key);
  }

  const nextRequest = request()
    .then((data) => {
      responseCache.set(key, {
        data,
        timestamp: Date.now()
      });
      return data;
    })
    .finally(() => {
      inflightRequests.delete(key);
    });

  inflightRequests.set(key, nextRequest);
  return nextRequest;
}

function invalidateCache(keyPrefixes) {
  for (const key of responseCache.keys()) {
    if (keyPrefixes.some((prefix) => key.startsWith(prefix))) {
      responseCache.delete(key);
    }
  }
}

function createCachedApi() {
  return {
    ...apiService,

    getTokenValidation() {
      return getCachedRequest('tokenValidation:data', () => apiService.getTokenValidation(), 10000);
    },

    getAdminUsers() {
      return getCachedRequest('adminUsers:all', () => apiService.getAdminUsers());
    },

    getAdminUserDetails(userEmail) {
      return getCachedRequest(`adminUsers:details:${userEmail}`, () =>
        apiService.getAdminUserDetails(userEmail)
      );
    },

    async updateAdminUser(reqType, userEmail) {
      const response = await apiService.updateAdminUser(reqType, userEmail);
      invalidateCache(['adminUsers:']);
      return response;
    },

    async updateRefreshToken(token, pin, appIDHash, clientID) {
      const response = await apiService.updateRefreshToken(token, pin, appIDHash, clientID);
      invalidateCache(['tokenValidation:']);
      return response;
    },

    clearApiCache() {
      responseCache.clear();
      inflightRequests.clear();
    }
  };
}

export function AppProvider({ children }) {
  const value = useMemo(
    () => ({
      api: createCachedApi()
    }),
    []
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext() {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }

  return context;
}
